import { useEffect, useRef, useState } from 'react'
import { Plus, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  useViewsStore,
  applyFilters,
  applyDefaultFilters,
} from '../stores/viewsStore'

export function ViewTabs() {
  const views = useViewsStore((s) => s.views)
  const activeViewId = useViewsStore((s) => s.activeViewId)
  const setActiveView = useViewsStore((s) => s.setActiveView)
  const createView = useViewsStore((s) => s.createView)
  const deleteView = useViewsStore((s) => s.deleteView)
  const renameView = useViewsStore((s) => s.renameView)

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  // Hydration: on mount, restore the active view's filters
  useEffect(() => {
    const { activeViewId, views } = useViewsStore.getState()
    if (activeViewId) {
      const view = views.find((v) => v.id === activeViewId)
      if (view) {
        applyFilters(view.filters)
      } else {
        useViewsStore.getState().setActiveView(null)
      }
    }
  }, [])

  // Focus input when editing starts
  useEffect(() => {
    if (editingId && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [editingId])

  function handleSwitchToAll() {
    setActiveView(null)
    applyDefaultFilters()
  }

  function handleSwitchToView(id: string) {
    const view = views.find((v) => v.id === id)
    if (!view) return
    setActiveView(id)
    applyFilters(view.filters)
  }

  function handleCreate() {
    const id = createView('Ny visning')
    setEditingId(id)
    setEditValue('Ny visning')
  }

  function handleDelete(e: React.MouseEvent, id: string) {
    e.stopPropagation()
    deleteView(id)
  }

  function handleDoubleClick(id: string, currentName: string) {
    setEditingId(id)
    setEditValue(currentName)
  }

  function commitRename() {
    if (!editingId) return
    const trimmed = editValue.trim()
    if (trimmed) {
      renameView(editingId, trimmed)
    }
    // If empty, keep old name (no change)
    setEditingId(null)
  }

  const isAllActive = activeViewId === null

  return (
    <div className="flex items-end gap-0.5 px-4 pt-1 overflow-hidden">
      {/* "Alle ordrer" — always first */}
      <button
        onClick={handleSwitchToAll}
        className={cn(
          'flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap',
          isAllActive
            ? 'bg-white border border-gray-200 border-b-white -mb-px z-10 text-gray-900'
            : 'bg-gray-100/60 text-gray-500 hover:text-gray-700 hover:bg-gray-100'
        )}
      >
        Alle ordrer
      </button>

      {/* Saved views */}
      {views.map((view) => {
        const isActive = activeViewId === view.id
        const isEditing = editingId === view.id

        return (
          <button
            key={view.id}
            onClick={() => !isEditing && handleSwitchToView(view.id)}
            onDoubleClick={() => handleDoubleClick(view.id, view.name)}
            className={cn(
              'group flex items-center gap-1.5 pl-4 pr-2 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap',
              isActive
                ? 'bg-white border border-gray-200 border-b-white -mb-px z-10 text-gray-900'
                : 'bg-gray-100/60 text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            )}
          >
            {isEditing ? (
              <input
                ref={inputRef}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={commitRename}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitRename()
                  if (e.key === 'Escape') setEditingId(null)
                }}
                className="w-24 px-1 py-0 text-sm font-medium bg-white border border-emerald-300 rounded outline-none focus:ring-1 focus:ring-emerald-400"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <span>{view.name}</span>
            )}
            <span
              onClick={(e) => handleDelete(e, view.id)}
              className={cn(
                'p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors',
                isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
              )}
            >
              <X size={13} />
            </span>
          </button>
        )
      })}

      {/* Add new view */}
      <button
        onClick={handleCreate}
        className="flex items-center px-2.5 py-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100/60 rounded-t-lg transition-colors"
        title="Ny visning"
      >
        <Plus size={16} />
      </button>
    </div>
  )
}
